import Storage from 'utils/localStorage/Storage';

const SESSION_EXPIRY = Storage.DAYS * 7;
const SUPERCHAT_CONFIG_EXPIRY = Storage.DAYS * 1;
const ANONYMOUS_EXPIRY = Storage.DAYS * 7;
const FOLLOW_UPS_EXPIRY = Storage.DAYS * 7;
const WELCOME_MAT_EXPIRY = Storage.DAYS * 1;
const PENDING_SIGN_IN_EXPIRY = 5 * Storage.MINUTES;
const ENTRY_SOURCE_EXPIRY = 30 * Storage.SECONDS;

const KEYS = {
    GENAI_SESSION_KEY: 'GENAI_SESSION',
    GENAI_ANONYMOUS_ID_KEY: 'GENAI_ANONYMOUS_ID',
    SUPERCHAT_CONFIG_KEY: 'SUPERCHAT_CONFIG',
    GENAI_LAST_FOLLOW_UPS_KEY: 'GENAI_LAST_FOLLOW_UPS',
    SUPERCHAT_REFERRER_KEY: 'SUPERCHAT_REFERRER',
    WELCOME_MAT_KEY: 'WELCOME_MAT',
    GENAI_PENDING_SIGN_IN_KEY: 'GENAI_PENDING_SIGN_IN',
    SUPERCHAT_ENTRY_SOURCE_KEY: 'SUPERCHAT_ENTRY_SOURCE'
};

// Shared marker values written by callers (e.g. ShopModal) and consumed by the
// SuperChat deeplink handler. Single source of truth — must not be duplicated as
// string literals elsewhere.
const SUPERCHAT_ENTRY_SOURCES = {
    SHOP_TAB_FEATURED: 'shopTabFeatured'
};

const setGENAISession = dataSession => {
    const session = JSON.stringify(dataSession);
    Storage.local.setItem(KEYS.GENAI_SESSION_KEY, session, SESSION_EXPIRY);
};

const setSuperChatConfig = config => {
    try {
        Storage.local.setItem(KEYS.SUPERCHAT_CONFIG_KEY, JSON.stringify(config), SUPERCHAT_CONFIG_EXPIRY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error creating Super Chat Config in LocalStorage');
    }
};

const setGENAIAnonymousId = (anonymousId, associatedBiId = null) => {
    const anonymousData = {
        anonymousId,
        associatedBiId
    };
    Storage.local.setItem(KEYS.GENAI_ANONYMOUS_ID_KEY, JSON.stringify(anonymousData), ANONYMOUS_EXPIRY);
};

const setLastFollowUps = (sessionId, followUps, context, followUpQuestion = '') => {
    if (!sessionId || !Array.isArray(followUps)) {
        return;
    }

    try {
        const payload = JSON.stringify({
            sessionId,
            followUps,
            followUpQuestion,
            context
        });
        Storage.local.setItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY, payload, FOLLOW_UPS_EXPIRY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error storing last follow ups in LocalStorage', error);
    }
};

const MS_HOUR = 60 * 60 * 1000;
const MS_30MIN = 30 * 60 * 1000;
const MS_10MIN = 10 * 60 * 1000;
const MS_5MIN = 5 * 60 * 1000;
const MS_1MIN = 60 * 1000;
const MS_30SEC = 30 * 1000;

const computeTimeLeft = expiresAt => {
    const remaining = Math.max((expiresAt || 0) - Date.now(), 0);

    if (remaining >= MS_HOUR) {
        return `${Math.floor(remaining / MS_HOUR)}h`;
    }

    if (remaining >= MS_30MIN) {
        return '30min';
    }

    if (remaining >= MS_10MIN) {
        return '10min';
    }

    if (remaining >= MS_5MIN) {
        return '5min';
    }

    if (remaining >= MS_1MIN) {
        return '1min';
    }

    if (remaining >= MS_30SEC) {
        return '30s';
    }

    return '0s';
};

const setWelcomeMat = () => {
    try {
        const expiresAt = Date.now() + WELCOME_MAT_EXPIRY;
        const welcomeMatData = JSON.stringify({
            shownAt: Date.now(),
            expiresAt,
            timeLeftToDisplay: computeTimeLeft(expiresAt)
        });
        Storage.local.setItem(KEYS.WELCOME_MAT_KEY, welcomeMatData, WELCOME_MAT_EXPIRY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error storing welcome mat data in LocalStorage', error);
    }
};

const getGENAISession = () => {
    const existingSession = Storage.local.getItem(KEYS.GENAI_SESSION_KEY);

    if (!existingSession) {
        return null;
    }

    return JSON.parse(existingSession);
};

const getSuperChatConfig = () => {
    const existingConfig = Storage.local.getItem(KEYS.SUPERCHAT_CONFIG_KEY);

    try {
        return JSON.parse(existingConfig);
    } catch (error) {
        return null;
    }
};

const getWelcomeMat = () => {
    return Storage.local.getItem(KEYS.WELCOME_MAT_KEY);
};

const getWelcomeMatTimeLeft = () => {
    const stored = Storage.local.getItem(KEYS.WELCOME_MAT_KEY, false, false, true);

    if (!stored?.data) {
        return '0s';
    }

    try {
        const { data, expiry } = stored;
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        const timeLeft = computeTimeLeft(parsed.expiresAt);

        parsed.timeLeftToDisplay = timeLeft;
        Storage.local.setItem(KEYS.WELCOME_MAT_KEY, JSON.stringify(parsed), expiry);

        return timeLeft;
    } catch {
        return '0s';
    }
};

const getLastFollowUps = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY);

    if (!storedValue) {
        return null;
    }

    try {
        return JSON.parse(storedValue);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error parsing stored follow ups from LocalStorage', error);
        removeLastFollowUps();

        return null;
    }
};

const getGENAIAnonymousId = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_ANONYMOUS_ID_KEY);

    if (!storedValue) {
        return null;
    }

    // Handle migration from old string format to new object format
    if (typeof storedValue === 'string' && storedValue.indexOf('{') !== 0) {
        // Old format: plain string anonymousId
        return storedValue;
    }

    try {
        const parsed = typeof storedValue === 'string' ? JSON.parse(storedValue) : storedValue;

        return parsed.anonymousId || null;
    } catch (error) {
        // If parsing fails, assume it's the old string format
        return storedValue;
    }
};

const getAssociatedBiId = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_ANONYMOUS_ID_KEY);

    if (!storedValue) {
        return null;
    }

    // Old format doesn't have BiId association
    if (typeof storedValue === 'string' && storedValue.indexOf('{') !== 0) {
        return null;
    }

    try {
        const parsed = typeof storedValue === 'string' ? JSON.parse(storedValue) : storedValue;

        return parsed.associatedBiId || null;
    } catch (error) {
        return null;
    }
};

const removeLastFollowUps = () => {
    Storage.local.removeItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY);
};

const removeSuperChatConfig = () => {
    try {
        Storage.local.removeItem(KEYS.SUPERCHAT_CONFIG_KEY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error removing Super Chat Config from LocalStorage');
    }
};

const removeGENAISession = () => {
    Storage.local.removeItem(KEYS.GENAI_SESSION_KEY);
    removeLastFollowUps();
};

const removeGENAIAnonymousId = () => {
    Storage.local.removeItem(KEYS.GENAI_ANONYMOUS_ID_KEY);
};

const contextsMatch = (storedContext, currentContext) => {
    if (!storedContext || !currentContext) {
        return false;
    }

    if (storedContext.entrypoint !== currentContext.entrypoint) {
        return false;
    }

    switch (storedContext.entrypoint) {
        case 'PDP':
            return storedContext.product_id === currentContext.product_id;
        case 'PLP':
            return storedContext.category_id === currentContext.category_id;
        case 'SRP':
            return storedContext.search_term === currentContext.search_term;
        case 'default':
            return true;
        default:
            return false;
    }
};

const getValidatedFollowUps = (sessionIdentifier, currentContext, prompts, areFollowUpsSameAsPromptsFn) => {
    if (!sessionIdentifier) {
        return null;
    }

    const storedFollowUps = getLastFollowUps();

    const sessionMatches = storedFollowUps?.sessionId === sessionIdentifier;
    const contextMatches = contextsMatch(storedFollowUps?.context, currentContext);

    if (!storedFollowUps || (!sessionMatches && !contextMatches)) {
        removeLastFollowUps();

        return null;
    }

    if (!Array.isArray(storedFollowUps.followUps) || storedFollowUps.followUps.length === 0) {
        removeLastFollowUps();

        return null;
    }

    if (areFollowUpsSameAsPromptsFn(storedFollowUps.followUps, prompts)) {
        removeLastFollowUps();

        return null;
    }

    return {
        followUps: storedFollowUps.followUps,
        followUpQuestion: storedFollowUps.followUpQuestion || ''
    };
};

const setSuperChatReferrer = referrer => {
    if (referrer) {
        Storage.session.setItem(KEYS.SUPERCHAT_REFERRER_KEY, referrer);
    }
};

const getSuperChatReferrer = () => {
    return Storage.session.getItem(KEYS.SUPERCHAT_REFERRER_KEY) || null;
};

// Cleans up when no longer needed to prevent stale data from affecting future analytics.
// referrer context should persist across page navigations but clear when the browser session ends.
const removeSuperChatReferrer = () => {
    Storage.session.removeItem(KEYS.SUPERCHAT_REFERRER_KEY);
};

const setPendingSignInEvent = (lastMessageId = null) => {
    Storage.local.setItem(KEYS.GENAI_PENDING_SIGN_IN_KEY, JSON.stringify({ lastMessageId }), PENDING_SIGN_IN_EXPIRY);
};

const getPendingSignInEvent = () => {
    const val = Storage.local.getItem(KEYS.GENAI_PENDING_SIGN_IN_KEY);

    if (!val) {
        return null;
    }

    if (val === 'true') {
        return { lastMessageId: null };
    }

    try {
        return JSON.parse(val);
    } catch {
        return null;
    }
};

const clearPendingSignInEvent = () => {
    Storage.local.removeItem(KEYS.GENAI_PENDING_SIGN_IN_KEY);
};

// Marks the next SuperChat deeplink-triggered open with the click origin
// (e.g. 'shopTabFeatured'). Short TTL — only the immediate page load after
// the click should consume this; stale markers are dropped.
const setSuperChatEntrySource = source => {
    if (source) {
        Storage.session.setItem(KEYS.SUPERCHAT_ENTRY_SOURCE_KEY, source, ENTRY_SOURCE_EXPIRY);
    }
};

// One-shot read: returns the stored value (if not expired) and clears it,
// so the next deeplink open won't pick up the same marker.
const consumeSuperChatEntrySource = () => {
    const value = Storage.session.getItem(KEYS.SUPERCHAT_ENTRY_SOURCE_KEY);

    if (value) {
        Storage.session.removeItem(KEYS.SUPERCHAT_ENTRY_SOURCE_KEY);
    }

    return value || null;
};

export {
    setGENAISession,
    setGENAIAnonymousId,
    setLastFollowUps,
    setSuperChatConfig,
    getGENAISession,
    getSuperChatConfig,
    getLastFollowUps,
    getValidatedFollowUps,
    removeSuperChatConfig,
    getGENAIAnonymousId,
    getAssociatedBiId,
    removeGENAISession,
    removeGENAIAnonymousId,
    removeLastFollowUps,
    setSuperChatReferrer,
    getSuperChatReferrer,
    removeSuperChatReferrer,
    setWelcomeMat,
    getWelcomeMat,
    getWelcomeMatTimeLeft,
    setPendingSignInEvent,
    getPendingSignInEvent,
    clearPendingSignInEvent,
    setSuperChatEntrySource,
    consumeSuperChatEntrySource,
    SUPERCHAT_ENTRY_SOURCES,
    KEYS
};
